import { UserType } from 'library/ts/model/user/UserType';
import { PlayerAdapterEvent } from 'library/ts/player/adapter/events/PlayerAdapterEvent';
import { IPlayerAdapterEventVO } from 'library/ts/player/adapter/interfaces/events/IPlayerAdapterEventVO';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { TagEventVO } from 'tag/ts/tag/events/TagEventVO';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { TagErrorCode } from 'tag/ts/tag/model/error/TagErrorCode';
import { AdSDK } from 'tag/ts/tag/model/type/ads/AdSDK';

/**
 * @copyright © Paramount Streaming
 * @author Zeeshan Ahmad
 * @description Ad Blocker manager class
 */
export class AdBlockerManager extends AbstractTagClass {

	/**
	 * @description Timeout ID for checking adblock status
	 */
	#timeoutId: any;

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'AdBlockerManager'
			}
		);
		const adSDKType = this.tag?.model?.adUtilities?.adSetupInfo()?.sdk?.type;

		this.utils.Delay.delay()
			.then(
				() => {
					if (
						(
							adSDKType === AdSDK.IMA
							||
							adSDKType === AdSDK.IMA_DAI
						)
						&&
						this.#adBlockParamsValid() === true
					) {
						this.#initEventHandler();
					}
				}
			);
	}

	/**
	 * @description Check if adblock params are valid
	 */
	#adBlockParamsValid = (): boolean => {
		return this.utils.Type.isFunction( this.params?.AD?.AB?.method ) === true &&
			this.utils.Type.isNullOrUndefined( this.params?.AD?.AB?.interval ) === false;
	};

	/**
	 * @description Setup event handler to check for ad blocker when ready
	 */
	#initEventHandler = (): void => {
		this.tagEventsHandler(
			this.#onTagEvent,
			[
				TagEvent.ON_AD_BLOCKER_DETECTED,
				TagEvent.ON_PLAYER_EVENT,
				TagEvent.ON_FATAL_ERROR,
				TagEvent.ON_REQUEST_AD_BLOCKER_CHECK_STOP
			]
		);
	};

	/**
	 * @description Start timeout for ad block check
	 */
	#startCheckTimeout = (): void => {
		this.#stopCheckTimeout();
		this.#timeoutId = setTimeout( this.#checkAdblockStatus, this.params.AD.AB.interval );
	};

	/**
	 * @description Clear timeout for ad block check
	 */
	#stopCheckTimeout = (): void => {
		clearTimeout( this.#timeoutId );
	};

	/**
	 * @description Check for ad blocker status
	 */
	#checkAdblockStatus = (): void => {
		const detected = this.params.AD.AB.method();
		this?.logger?.log( 'checkAdblockStatus:', 'ad blocker check on page result:', detected );

		switch ( detected ) {
			case true:
				this.#emitAdblockerDetected();
				break;
			case false:
				this.#startCheckTimeout();
				break;
		}
	};

	/**
	 * @description Detected adblock
	 */
	#emitAdblockerDetected = (): void => {
		this.dispatchTagEvent(
			this.getTagEventVO(
				TagEvent.ON_AD_BLOCKER_DETECTED,
				this.tag
			)
		);
	};

	/**
	 * @description Tag application event handler
	 * @param {TagEventVO} event
	 */
	#onTagEvent = ( event: TagEventVO ): void => {
		const logEvent = ( event: TagEventVO ): void => {
			this?.logger?.log( '#onTagEvent', event );
		};

		switch ( event?.type ) {
			case TagEvent.ON_PLAYER_EVENT:
				this.#onPlayerEvent( event?.data?.playerAdapterEvent );
				break;
			case TagEvent.ON_AD_BLOCKER_DETECTED:
				logEvent( event );
				this.#onAdblockDetected();
				break;
			case TagEvent.ON_FATAL_ERROR:
				logEvent( event );
				this.#onFatalError();
				break;
			case TagEvent.ON_REQUEST_AD_BLOCKER_CHECK_STOP:
				logEvent( event );
				this.#onRequestAdBlockerCheckStop();
				break;
		}
	};

	/**
	 * @description Player event handler
	 * @param {IPlayerAdapterEventVO} event
	 */
	#onPlayerEvent = ( event: IPlayerAdapterEventVO ): void => {
		// filter by player event type
		switch ( event?.type ) {
			case PlayerAdapterEvent.RESOURCE_START:
				// user status converted to our internal model + stringified for comparison
				const userTypeString = this.tag.tagUtilities.getUserType( this?.params?.USER?.userStatus );

				// filter by user type
				switch ( userTypeString ) {
					// start ad blocker check only for anon, LCP and LC users
					case UserType.ANON:
					case UserType.LC:
					case UserType.LCP:
					case UserType.MVPD_AUTHZ:
						this.utils.Delay.delay( this.params.AD.AB.delay )
							.then( () => this.#startCheckTimeout() );
						break;
				}
				break;
			case PlayerAdapterEvent.RESOURCE_END:
				this.dispatchTagEvent(
					this.getTagEventVO(
						TagEvent.ON_REQUEST_AD_BLOCKER_CHECK_STOP,
						this.tag,
						undefined,
						this,
						this.name
					)
				);
				break;
		}
	};

	/**
	 * @description Stop VTG Player session, dispatch fatal error
	 */
	#onAdblockDetected = (): void => {
		this?.logger?.log( 'onAdblockDetected' );

		this.#stopCheckTimeout();

		// request player stop via event
		this.tag.dispatchTagEvent(
			// request player stop
			this.tag.getTagEventVO(
				TagEvent.ON_TAG_PLAYER_STOP_REQUEST,
				this?.tag,
				undefined,
				this,
				this?.name
			)
		);

		let errorCode: string;
		// user status converted to our internal model + stringified for comparison
		const userTypeString = this.tag.tagUtilities.getUserType( this?.params?.USER?.userStatus );

		switch ( userTypeString ) {
			case UserType.ANON:
				errorCode = TagErrorCode.AD_BLOCKER_CBS_ANON_USER;
				break;
			case UserType.LC:
			case UserType.LCP:
			case UserType.MVPD_AUTHZ:
				errorCode = TagErrorCode.AD_BLOCKER_CBS_LC_USER;
				break;
		}

		this.dispatchTagEvent(
			this.getTagEventVO(
				TagEvent.ON_FATAL_ERROR,
				this.tag,
				{
					fatalError: {
						code: errorCode,
						msg: {
							original: undefined,
							custom: this.tag.errorManager.createErrorMessageByCode( errorCode )
						}
					}
				}
			)
		);
	};

	/**
	 * @description Stop check on error
	 */
	#onFatalError = (): void => {
		this?.logger?.log( 'onFatalError' );
		this.#stopCheckTimeout();
	};

	/**
	 * @description Stop check on request
	 */
	#onRequestAdBlockerCheckStop = (): void => {
		this?.logger?.log( 'onRequestAdBlockerCheckStop' );
		this.#stopCheckTimeout();
	};
}